import {
  Alert,
  Box,
  Snackbar,
  SxProps,
  TextFieldProps,
  Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import React from "react";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import PCWorthMapOverlay from "./PCWorthMapOverlay";
import {
  initialZoom,
  mapLibraries,
  pcWorthLatLng,
} from "common/constants/maps";
import { GOOGLE_MAPS_API_KEY } from "common";
import { GoogleLatLng } from "common/types/google-maps";
import KachiMapMarker from "./KachiMapMarker";
import MapAutoComplete from "./MapAutoComplete";
import { mapOptions } from "common/constants/maps/map-options";
import { filterGeoCodePHOnly } from "utils/maps";
import { Small } from "components/Typography";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CheckOutMapAutoComplete from "./CheckOutMapAutoComplete";
import { GeolocationProps } from "modules/location/types";

// ==============================================================
type Props = {
  containerStyle?: SxProps<Theme>;
  onCenterChangeWithIdle?: (geoCodeVal: google.maps.GeocoderResult) => void;
  hasInstructionAlert?: boolean;
  geoCodeVal?: google.maps.GeocoderResult; // NOTE: geoCodeVal is used if ever we reached google maps query limit
  onCenterChange?: (v: GoogleLatLng) => void;
  onMapRefChange?: (ref: google.maps.Map) => void;
  mapAutoCompleteBounds?:
    | google.maps.LatLngBounds
    | google.maps.LatLngBoundsLiteral;

  deliveryAddressErr?: string;
  onDeliveryAddressInputRefChange?: (
    ref?: React.MutableRefObject<TextFieldProps>
  ) => void;
  onDeliveryAddressChange?: (v?: string) => void;
  hideSearchBar?: boolean;
  searchBarVariant?: "default" | "checkout";
  onMarkerRefChange?: (markerRef: google.maps.Marker) => void;

  //passing isCreateAddress for logic of input value of checkoutAutoComplete
  isCreateAddress?: boolean;

  address?: string;
  onHandleGetProvince?: (geolocation: GeolocationProps) => void;
};
// ==============================================================

const GoogleMapComponent = ({
  hasInstructionAlert = true,
  hideSearchBar = false,
  geoCodeVal,
  containerStyle,
  onCenterChange,
  onCenterChangeWithIdle,
  onMapRefChange,
  mapAutoCompleteBounds,
  searchBarVariant = "default",
  onMarkerRefChange,
  deliveryAddressErr,
  onDeliveryAddressChange,
  onDeliveryAddressInputRefChange,
  isCreateAddress,
  address,
  onHandleGetProvince,
}: Props) => {
  const [mapRef, setMapRef] = useState<google.maps.Map | undefined>();
  const [autoCompleteRef, setAutoCompleteRef] = useState<
    google.maps.places.Autocomplete | undefined
  >();

  const [isScriptLoaded, setScriptLoaded] = useState(false);
  const [centerState, setCenterState] = useState<GoogleLatLng | undefined>();
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);

  const panToLatLng = (googleLatLng: GoogleLatLng) => {
    if (mapRef && googleLatLng) {
      mapRef?.panTo(googleLatLng);
      mapRef?.setZoom(initialZoom);
    }
  };

  const onIdle = async () => {
    if (onCenterChangeWithIdle) {
      let province = "";
      let region = "";
      const geoCode = await getGeoCodeViaLatLng();

      if (geoCode) {
        province = geoCode.address_components?.find((comp) =>
          comp.types.includes("administrative_area_level_2")
        )?.long_name;

        region = geoCode.address_components?.find((comp) =>
          comp.types.includes("administrative_area_level_1")
        )?.long_name;

        const city = geoCode.address_components?.find((comp) =>
          comp.types.includes("locality")
        )?.long_name;

        if (!province) {
          //gmaps tagged metro manila as province we catch it here
          province = geoCode.address_components?.find((comp) =>
            comp.types.includes("administrative_area_level_1")
          )?.long_name;
        }

        if (onHandleGetProvince) {
          onHandleGetProvince({ region, province, city });
        }

        if (geoCode?.formatted_address) {
          onCenterChangeWithIdle(geoCode);
          setSearchInputValue(geoCode?.formatted_address);
        }
      } else if (geoCodeVal) {
        setIsSnackBarOpen(true);

        // NOTE: if ever we reached google maps query limit geoCodeVal(previous) is used
        onCenterChangeWithIdle(geoCodeVal);
        setSearchInputValue(geoCodeVal?.formatted_address);

        const prevLatLng = geoCodeVal?.geometry?.location?.toJSON();
        panToLatLng(prevLatLng);
      }
    }
  };

  const getGeoCodeViaLatLng = async () => {
    // centerState to know if mag is already dragged
    if (isScriptLoaded && mapRef && centerState) {
      const geocoder = new google.maps.Geocoder();
      const location = mapRef?.getCenter();
      const bounds = mapRef?.getBounds();

      if (geocoder) {
        const res: google.maps.GeocoderResult | undefined = await geocoder
          .geocode({ location, bounds })
          .then((reversedGeoCode) => {
            if (reversedGeoCode?.results?.length > 0) {
              const philippinesOnly = filterGeoCodePHOnly(
                reversedGeoCode?.results
              );
              if (philippinesOnly?.length > 0) {
                return philippinesOnly[0];
              }
            }
            return undefined;
          })
          .catch((err) => {
            return undefined;
          });
        return res;
      }
    }
    return undefined;
  };

  const onCenterChangeInternal = () => {
    if (mapRef) {
      const center = mapRef?.getCenter();
      setCenterState(center);
      if (onCenterChange) {
        onCenterChange(center);
      }
    }
  };

  const onGoogleMapLoad = (newMapRef: google.maps.Map) => {
    setMapRef(newMapRef);
    if (onMapRefChange) {
      onMapRefChange(newMapRef);
    }
  };

  const onAutoCompletePlaceChanged = (address?: string) => {
    if (autoCompleteRef && mapRef) {
      const place = autoCompleteRef?.getPlace();
      const viewPortBounds = place?.geometry?.viewport;
      const formattedAddress = place?.formatted_address;

      if (viewPortBounds) {
        mapRef?.fitBounds(viewPortBounds);
        mapRef?.panToBounds(viewPortBounds);
      }

      mapRef?.setZoom(initialZoom);

      const inputSource = (
        document.getElementById(
          "google_checkout_autocomplete"
        ) as HTMLInputElement
      )?.value;

      setSearchInputValue(inputSource || formattedAddress || "");
    }
  };

  const fetchCurrentAddressGeoCode = useCallback(
    (address?: string) => {
      if (!address || !isScriptLoaded || !mapRef) return;

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results?.length) {
          const location = results[0].geometry.location;
          mapRef.setCenter(location);
          mapRef.setZoom(initialZoom);

          if (onMarkerRefChange) {
            onMarkerRefChange(
              new google.maps.Marker({ position: location, map: mapRef })
            );
          }

          setSearchInputValue(results[0].formatted_address);
        } else {
          console.error("Geocode was not successful: ", status);
        }
      });
    },
    [isScriptLoaded, mapRef, onMarkerRefChange]
  );

  useEffect(() => {
    if (address) {
      fetchCurrentAddressGeoCode(address);
    }
  }, [address, fetchCurrentAddressGeoCode]);

  return (
    <Box>
      {!hideSearchBar && isScriptLoaded && searchBarVariant === "checkout" ? (
        <CheckOutMapAutoComplete
          inputValue={searchInputValue}
          onLoad={(ref) => setAutoCompleteRef(ref)}
          autoCompleteBounds={mapAutoCompleteBounds}
          onPlaceChanged={onAutoCompletePlaceChanged}
          onInputRefChange={(ref) => {
            if (onDeliveryAddressInputRefChange) {
              onDeliveryAddressInputRefChange(ref);
            }
          }}
          setInputValue={(v) => {
            setSearchInputValue(v);
            onDeliveryAddressChange(v);
          }}
          autoCompleteErr={deliveryAddressErr}
          isCreateAddress={isCreateAddress}
        />
      ) : null}

      {hasInstructionAlert ? (
        <Alert
          icon={<LocationOnOutlinedIcon />}
          severity="info"
          sx={{
            p: 0,
            px: 0.5,
            border: 0.5,
            marginBottom: 0.5,
          }}
        >
          <Small fontWeight={600}>
            {/* Inform user to use the textfield to accurately input address data */}
            {
              "To ensure a smooth and timely delivery, please pin the correct location. Providing an accurate address helps us deliver your products efficiently and without delays."
            }
          </Small>
        </Alert>
      ) : null}
      <Box sx={containerStyle}>
        <LoadScript
          libraries={mapLibraries}
          onLoad={() => setScriptLoaded(true)}
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        >
          {isScriptLoaded ? (
            <GoogleMap
              onIdle={onIdle}
              zoom={initialZoom}
              options={mapOptions}
              center={pcWorthLatLng}
              clickableIcons={false}
              onLoad={onGoogleMapLoad}
              onCenterChanged={onCenterChangeInternal}
              mapContainerStyle={{
                display: "flex",
                width: "100%",
                height: 400,
              }}
            >
              <KachiMapMarker
                onMarkerRefChange={onMarkerRefChange}
                centerState={centerState}
              />

              {!hideSearchBar && searchBarVariant === "default" ? (
                <MapAutoComplete
                  hidden={hideSearchBar}
                  inputValue={searchInputValue}
                  onLoad={(ref) => setAutoCompleteRef(ref)}
                  autoCompleteBounds={mapAutoCompleteBounds}
                  onPlaceChanged={onAutoCompletePlaceChanged}
                  setInputValue={(v) => setSearchInputValue(v)}
                />
              ) : null}

              <PCWorthMapOverlay mapRef={mapRef} />
            </GoogleMap>
          ) : null}
        </LoadScript>

        <Snackbar
          open={isSnackBarOpen}
          autoHideDuration={3000}
          onClose={() => setIsSnackBarOpen(false)}
        >
          <Alert severity="error">
            Please use search box to search for location.
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default GoogleMapComponent;
